import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { DropdownItem } from '../../../bdo/models/dropdownItem';
import { FormGroup, FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';

@Component({
    selector: 'bdo-dropdown',
    templateUrl: './dropdown.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: DropdownComponent
        }
    ],
    styleUrls: ['./dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
/** A Dropdown containing Strings and preselecting the given selected one unless required */
export class DropdownComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() data: Array<DropdownItem>;
  @Input() forceValidate: boolean = false;
  @Input() requiredErrorText: string;
  @Input() selected: string = null; // Select the first item as selected and emit value unless noPreselection is true
  @Input() required: boolean = false; // = noPreSelection & placeholder = label
  @Input() labelText: string = '';
  @Input() ariaLabel: string = ''; // Set ariaLabel for a11y if labelText is not set
  @Input() dataQa: string = '';
  @Input() inputName: string;

  @Output() itemSelected: EventEmitter<any> = new EventEmitter();

  public form = new FormGroup({
    selected: new FormControl(this.selected)
  });

  public errorMessageId: string;

  constructor() { }

  writeValue(value: any): void {
    if (value !== undefined && value !== null) {
      this.form.controls.selected.setValue(value, { emitEvent: false });
      this.selected = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    this.form.controls.selected.valueChanges.subscribe(value => {
      this.onChange(value);
    });
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
    this.form.controls.selected.valueChanges.subscribe(() => {
      this.onTouched();
    });
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.controls.selected.disable();
    } else {
      this.form.controls.selected.enable();
    }
  }

  ngOnInit(): void {
    this.errorMessageId = 'dropdown-error-' + uuidv4();
    if (!this.inputName) {
      this.inputName = 'input-' + uuidv4();
    }

    if (!this.selected && this.data && !this.required) {
      this.form.controls.selected.setValue(this.data[0]?.value);
    }
    if (this.required && !this.selected) {
      this.form.controls.selected.setValue('');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data?.currentValue && !this.form.controls.selected.value && !this.required) {
      const newValue: string = this.selected ? this.selected : changes.data?.currentValue[0]?.value;
      this.form.controls.selected.setValue(newValue);
      this.itemSelected.emit(this.form.controls.selected.value);
    }
    if (changes.selected?.currentValue) {
      this.form.controls.selected.setValue(this.selected);
    }
  }

  selectItem(event: Event) {
    this.itemSelected.emit((event.target as HTMLInputElement).value);
  }

  shouldShowError(): boolean {
    const control = this.form?.controls?.selected;
    if (!this.required || !control) {
      return false;
    }
    if (!control.invalid) {
      return false;
    }
    const touchedInvalid = control.touched && control.invalid;
    const forceInvalid = this.forceValidate && control.invalid;
    return touchedInvalid || forceInvalid;
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

}
